import { addDays, isWeekend, isoDate, monthOf, weekday, yearOf } from './dates'

function nthWeekdayOfMonth(year: number, month: number, targetWeekday: number, n: number): string {
  const firstWeekday = weekday(isoDate(year, month, 1))
  const delta = (targetWeekday - firstWeekday + 7) % 7
  return isoDate(year, month, 1 + delta + (n - 1) * 7)
}

function lastWeekdayOfMonth(year: number, month: number, targetWeekday: number): string {
  const last = new Date(Date.UTC(year, month, 0))
  const lastDay = last.getUTCDate()
  const lastWeekday = last.getUTCDay()
  const delta = (lastWeekday - targetWeekday + 7) % 7
  return isoDate(year, month, lastDay - delta)
}

/** Anonymous Gregorian computus. Returns Easter Sunday. */
export function easterSunday(year: number): string {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return isoDate(year, month, day)
}

export interface PublicHoliday {
  date: string
  name: string
}

/** The 10 public holidays listed by Citizens Information. A weekend date stays on that date. */
export function publicHolidays(year: number): PublicHoliday[] {
  const februaryFirst = isoDate(year, 2, 1)
  return [
    { date: isoDate(year, 1, 1), name: "New Year's Day" },
    { date: weekday(februaryFirst) === 5 ? februaryFirst : nthWeekdayOfMonth(year, 2, 1, 1), name: "St Brigid's Day" },
    { date: isoDate(year, 3, 17), name: "St Patrick's Day" },
    { date: addDays(easterSunday(year), 1), name: 'Easter Monday' },
    { date: nthWeekdayOfMonth(year, 5, 1, 1), name: 'May public holiday' },
    { date: nthWeekdayOfMonth(year, 6, 1, 1), name: 'June public holiday' },
    { date: nthWeekdayOfMonth(year, 8, 1, 1), name: 'August public holiday' },
    { date: lastWeekdayOfMonth(year, 10, 1), name: 'October public holiday' },
    { date: isoDate(year, 12, 25), name: 'Christmas Day' },
    { date: isoDate(year, 12, 26), name: "St Stephen's Day" },
  ]
}

export function publicHolidayOn(iso: string): PublicHoliday | undefined {
  return publicHolidays(yearOf(iso)).find((holiday) => holiday.date === iso)
}

export function isPublicHoliday(iso: string): boolean {
  return publicHolidayOn(iso) !== undefined
}

export function irishPublicHolidays(year: number): Set<string> {
  const holidays = new Set(publicHolidays(year).map((holiday) => holiday.date))
  const christmas = isoDate(year, 12, 25)
  const stephens = isoDate(year, 12, 26)
  if (weekday(stephens) === 6) holidays.add(addDays(stephens, 2))
  if (weekday(stephens) === 0) holidays.add(addDays(stephens, 1))
  if (weekday(christmas) === 6) holidays.add(addDays(christmas, 2))
  if (weekday(christmas) === 0) holidays.add(addDays(christmas, 1))
  return holidays
}

export function isIrishPublicHoliday(iso: string): boolean {
  return irishPublicHolidays(yearOf(iso)).has(iso)
}

export function isWorkingDay(iso: string): boolean {
  return !isWeekend(iso) && !isIrishPublicHoliday(iso)
}

export function priorWorkingDay(iso: string): string {
  let cursor = iso
  while (!isWorkingDay(cursor)) {
    cursor = addDays(cursor, -1)
  }
  return cursor
}

/** Payday is the 28th, or the nearest earlier working day. */
export function paydayForMonth(year: number, month: number): string {
  return priorWorkingDay(isoDate(year, month, 28))
}

export function reportDueDate(payday: string): string {
  return priorWorkingDay(addDays(payday, -7))
}

export function previousPayday(payday: string): string {
  let year = yearOf(payday)
  let month = monthOf(payday) - 1
  if (month < 1) {
    month = 12
    year -= 1
  }
  return paydayForMonth(year, month)
}

export function payPeriodForPayday(payday: string): { start: string; end: string } {
  return { start: addDays(previousPayday(payday), 1), end: payday }
}

export function upcomingPayday(today: string): string {
  const thisMonth = paydayForMonth(yearOf(today), monthOf(today))
  if (today <= thisMonth) return thisMonth
  let year = yearOf(today)
  let month = monthOf(today) + 1
  if (month > 12) {
    month = 1
    year += 1
  }
  return paydayForMonth(year, month)
}

export function shiftMonth(month: string, delta: number): string {
  const year = Number(month.slice(0, 4))
  const monthNumber = Number(month.slice(5, 7))
  const date = new Date(Date.UTC(year, monthNumber - 1 + delta, 1))
  return isoDate(date.getUTCFullYear(), date.getUTCMonth() + 1, 1).slice(0, 7)
}
